import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ChangeEvent, FC, SyntheticEvent, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import CancelIcon from '@mui/icons-material/Cancel'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import FilePresentIcon from '@mui/icons-material/FilePresent'
import {
  Button,
  Box,
  Typography,
  LinearProgress,
  IconButton,
  Tooltip,
  Divider,
  TextField,
  Tabs,
  Tab,
} from '@mui/material'

import { uploadFileWithProgress } from '../../utils/uploadFile'

interface IProps {
  isLoading: boolean
  error: Error | null
}

const FileUploaderServer: FC<IProps> = ({ isLoading, error }) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [file, setFile] = useState<File | null>(null)
  const [progress, setProgress] = useState<number>(0)
  const [inputValue, setInputValue] = useState<string>('')
  const [tabValue, setTabValue] = useState('file')
  const inputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()

  const handleResetFile = () => {
    const params = new URLSearchParams()
    setSearchParams(params)
  }
  const mutation = useMutation({
    mutationFn: (file: File) => uploadFileWithProgress(file, setProgress),
    onSuccess: (data) => {
      setProgress(0)
      setFilenameToUrl(data?.meta?.name)
      queryClient.invalidateQueries({ queryKey: ['table-data'] })
      setFile(null)
    },
    onError: () => {
      setProgress(0)
    },
  })

  const setFilenameToUrl = (nameOfFile: string) => {
    const params = new URLSearchParams()
    if (nameOfFile !== '') {
      params.set('ts', Date.now().toString())
      params.set('nameOfFile', nameOfFile)
    }
    setSearchParams(params)
  }

  const handleTabChange = (_event: SyntheticEvent, newValue: string) => {
    setTabValue(newValue)
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0]
    if (selected) {
      const isJsonOrGz = selected.name.endsWith('.json') || selected.name.endsWith('.gz')
      if (isJsonOrGz) {
        setFile(selected)
        mutation.reset()
      } else {
        alert('Please select a valid JSON or GZ file.')
      }
    }
  }

  const handleUpload = () => {
    if (file) {
      mutation.mutate(file)
    }
  }
  const triggerFileSelect = () => {
    inputRef.current?.click()
  }
  return (
    <Box
      sx={{
        borderRadius: '14px',
        backgroundColor: '#ffffff',
        p: '14px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '24px',
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="section tabs"
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab label="Upload File" value="file" />
          <Tab label="Enter file name" value="unidentified" />
        </Tabs>
        <Box sx={{ display: 'flex', gap: '16px', height: '36px', alignItems: 'center' }}>
          {tabValue === 'file' ? (
            <Button
              onClick={handleUpload}
              variant="contained"
              disabled={!file || mutation.status === 'pending'}
            >
              {mutation.status === 'pending' ? 'Uploading...' : 'Upload File'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={() => {
                setFilenameToUrl(inputValue)
                setInputValue('')
              }}
              disabled={!inputValue || isLoading}
            >
              Get data
            </Button>
          )}

          <Button
            variant="contained"
            disabled={!searchParams.get('nameOfFile')}
            onClick={() => {
              handleResetFile()
            }}
          >
            Reset file
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          height: '8px',
        }}
      >
        {mutation.status === 'pending' ? (
          <LinearProgress variant="determinate" value={progress} />
        ) : (
          <Divider />
        )}
      </Box>

      {tabValue === 'file' ? (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '16px',
            cursor: 'pointer',
            height: '40px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '16px',
              cursor: 'pointer',
            }}
          >
            <input
              type="file"
              accept=".json,.gz,application/gzip"
              onChange={handleFileChange}
              id="file-upload"
              ref={inputRef}
              style={{ display: 'none' }}
            />
            <Tooltip title="Select JSON file">
              <IconButton onClick={triggerFileSelect}>
                {file ? (
                  <FilePresentIcon fontSize="medium" />
                ) : (
                  <CloudUploadIcon fontSize="medium" />
                )}
              </IconButton>
            </Tooltip>
            <Typography component="label" htmlFor="file-upload" sx={{ cursor: 'pointer' }}>
              {file ? file.name : 'Select file'}
            </Typography>
            {file && (
              <CancelIcon
                fontSize={'small'}
                onClick={(e) => {
                  e.stopPropagation()
                  setFile(null)
                  mutation.reset()
                  if (inputRef.current) {
                    inputRef.current.value = ''
                  }
                }}
              />
            )}
          </Box>
          {mutation.error && (
            <Typography color="error">
              {(mutation.error as Error).message || 'Unknown error'}
            </Typography>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            height: '40px',
          }}
        >
          <TextField
            label="Enter the file name"
            variant="outlined"
            size="small"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value.trim())
            }}
            sx={{ flex: 1, maxWidth: '80%' }}
          />
          {!!error?.message && <Typography color="error">{error?.message}</Typography>}
        </Box>
      )}
    </Box>
  )
}

export default FileUploaderServer
